import { t as translate } from '@if/core/i18n'
import { formatCurrencyNoDecimals, formatPercentage } from '@if/core/utils'
import * as yup from '@if/core/yup'
import {
  ActionButton,
  AreaButton,
  Banner,
  BodyText,
  Caption,
  CompactDisplayValue,
  ControlledNumberField,
  ControlledPercentField,
  ControlledPercentSlider,
  ControlledChipsField,
  ControlledSwitch,
  DataField,
  FieldSectionTitle,
  FieldsetCard,
  FormContainer,
  FormSubmitButton,
  Icon,
  OverlineCardHeader,
  StatCard,
  StepProgressMeter,
  useConfirmDialog,
  useConfirmDiscardDialog,
  useDebouncedWatch,
  useFormContainerContext,
} from '@if/mui'
import { Box, Card, CardContent, Divider, Grid, Skeleton, Stack } from '@mui/material'
import { useMerchant } from '@src/containers/App/MerchantProvider'
import {
  useCreateCustomBuydownPlan,
  useRequestCustomBuydownSwitch,
  useCustomBuydownTermRates,
  useTerminateCustomBuydownPlan,
} from '@src/data/api/custom-buydown-api/custom-buydown-api'
import { type BorrowerContext, useBorrowerContext } from '@src/hooks/borrower-context-hooks'
import { CustomBuydownPlan, CustomBuydownSwitchStatus } from '@src/types/CustomBuydown'
import { EMerchantType } from '@if/core/types'
import { AlertTriangle, CircleStop, Lock, Plus, Settings2, ToggleRight } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { type Control, type UseFormTrigger, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useBlocker } from 'react-router-dom'
import { formatEasternDateTime } from '../planHistory'
import { getPromoPlanErrorCode, getPromoPlanFieldErrorMessages, PromoPlanErrorCode } from '../promoPlanErrorCodes'

const CUSTOM_BUYDOWN_OWNED_FIELD = 'CustomBuydown'

const MIN_CONTRIBUTION = 0.005
const MAX_CONTRIBUTION = 0.3
const MIN_LOAN_AMOUNT = 500
const ADMIN_FEE_RATE = 0.06
const COST_REFERENCE_AMOUNT = 10000
const CONTRIBUTION_MARKS = [0, 5, 10, 15, 20, 25, 30].map((value) => ({ value, label: `${value}%` }))
const TERM_SKELETON_COUNT = 5
const TERM_TILE_HEIGHT = 72

const APPROVAL_MODEL = { P_MIN: 0.3, P_MAX: 0.8, LAMBDA: 10 } as const
const conversionProbability = (contribution: number): number =>
  APPROVAL_MODEL.P_MIN + (APPROVAL_MODEL.P_MAX - APPROVAL_MODEL.P_MIN) * (1 - Math.exp(-APPROVAL_MODEL.LAMBDA * contribution))
const APPROVAL_TIERS = [
  { threshold: 0, level: 'marginal', color: 'error' },
  { threshold: 0.015, level: 'low', color: 'warning' },
  { threshold: 0.03, level: 'moderate', color: 'warning' },
  { threshold: 0.05, level: 'good', color: 'info' },
  { threshold: 0.1, level: 'high', color: 'info' },
  { threshold: 0.15, level: 'optimal', color: 'success' },
  { threshold: 0.2, level: 'veryHigh', color: 'success' },
] as const

const previewPlanCode = (contribution: number, adminFeeRate: number) => `FLASH${Math.round(contribution * 10000)}/${adminFeeRate * 100}`

// The switch fields ride in the same schema as the plan so one Save commits both. They are inert for
// a merchant already on Custom Buydown: optIn defaults true and the email is never read.
const planSchema = (maxLoanAmount: number) =>
  yup.object({
    contribution: yup.percent().required().min(MIN_CONTRIBUTION).max(MAX_CONTRIBUTION),
    maxFinancingAmount: yup
      .number()
      .min(MIN_LOAN_AMOUNT)
      .max(maxLoanAmount, () =>
        translate('customBuydown.errors.maxFinancingAmountTooHigh', {
          amount: formatCurrencyNoDecimals(maxLoanAmount),
        }),
      )
      .defined()
      .nullable(),
    optIn: yup.boolean().required(),
    // Same shape Manage Merchant edits (Merchant.promoNotificationEmailList): the merchant sees the
    // addresses already on file and adds or removes from that list. At least one is required only
    // once they opt in, so a switched merchant is never blocked by a field their page never renders.
    contactEmails: yup
      .array()
      .of(yup.text().email().required())
      .required()
      .when('optIn', { is: true, then: (schema) => schema.min(1) })
      .default([]),
  })

type PlanFormValues = yup.InferSubmit<ReturnType<typeof planSchema>>
type PlanDraftValues = yup.InferDraft<ReturnType<typeof planSchema>>

const getDefaults = (plan: CustomBuydownPlan | null, switchStatus: CustomBuydownSwitchStatus): PlanFormValues => ({
  contribution: plan?.merchantFeeRate ?? 0.0499,
  maxFinancingAmount: plan?.promoTermRates[0]?.maxFinancingAmount ?? null,
  // Off on every load for an unswitched merchant, so the toggle is always a deliberate act.
  optIn: switchStatus.isSwitched,
  contactEmails: switchStatus.contactEmails,
})

const RatePreview = ({
  merchantId,
  control,
  adminFeeRate,
  maxLoanAmount,
  context,
}: {
  merchantId: string
  control: Control<PlanDraftValues>
  adminFeeRate: number
  maxLoanAmount: number
  context: BorrowerContext
}) => {
  const { t } = useTranslation()

  const debouncedContribution = useDebouncedWatch({ control, name: 'contribution' }) ?? 0
  const debouncedMaxAmount = useDebouncedWatch({ control, name: 'maxFinancingAmount' })
  const canPreview =
    debouncedContribution >= MIN_CONTRIBUTION &&
    debouncedContribution <= MAX_CONTRIBUTION &&
    (debouncedMaxAmount == null || (debouncedMaxAmount >= MIN_LOAN_AMOUNT && debouncedMaxAmount <= maxLoanAmount))

  const { data: rates, isLoading } = useCustomBuydownTermRates(
    merchantId,
    debouncedContribution,
    adminFeeRate,
    debouncedMaxAmount ?? undefined,
  )
  const showSkeletons = !canPreview || (isLoading && !rates)

  return (
    <Box>
      <FieldSectionTitle>{t('customBuydown.ratesTitle', { context })}</FieldSectionTitle>
      <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 1.5 }}>
        {showSkeletons
          ? Array.from({ length: TERM_SKELETON_COUNT }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={TERM_TILE_HEIGHT} />
            ))
          : rates?.map((rate) => (
              <StatCard
                key={rate.termMonths}
                size="sm"
                accent
                label={t('customBuydown.termLabel', { months: rate.termMonths })}
                value={`${formatPercentage(rate.promoRate * 100)}%`}
                sx={{ height: TERM_TILE_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              />
            ))}
      </Box>
    </Box>
  )
}

const ContributionSection = ({ control, context }: { control: Control<PlanDraftValues>; context: BorrowerContext }) => {
  const { t } = useTranslation()

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
        <Box>
          <FieldSectionTitle>{t('customBuydown.contributionLabel')}</FieldSectionTitle>
          <Caption sx={{ display: 'block' }}>{t('customBuydown.contributionCardSubtitle', { context })}</Caption>
        </Box>
        <ControlledPercentField control={control} name="contribution" size="small" sx={{ width: 110 }} />
      </Stack>
      <Box sx={{ px: 1, mt: 2 }}>
        <ControlledPercentSlider
          control={control}
          name="contribution"
          min={MIN_CONTRIBUTION * 100}
          max={MAX_CONTRIBUTION * 100}
          step={0.01}
          marks={CONTRIBUTION_MARKS}
        />
      </Box>
    </Box>
  )
}


/**
 * Shown to a merchant still on the legacy promo plans. Flipping the toggle commits nothing — it only
 * unlocks Save — and the request reaches Client Relations when the merchant saves the page, alongside
 * whatever plan they built. Until an agent confirms, nothing about the merchant's offers changes.
 */
const SwitchSection = ({
  control,
  trigger,
  hasLegacyPlans,
  disabled,
}: {
  control: Control<PlanDraftValues>
  trigger: UseFormTrigger<PlanDraftValues>
  hasLegacyPlans: boolean
  disabled?: boolean
}) => {
  const { t } = useTranslation()

  return (
    <FieldsetCard icon={ToggleRight} title={t('customBuydown.switchRequest.title')}>
      <Stack spacing={2}>
        <BodyText>{t('customBuydown.switchRequest.description')}</BodyText>
        <BodyText>{t('customBuydown.switchRequest.notificationInfo')}</BodyText>

        <ControlledSwitch
          control={control}
          name="optIn"
          label={t('customBuydown.switchRequest.toggleLabel')}
          disabled={disabled}
        />

        {hasLegacyPlans && (
          <Banner severity="warning" icon={AlertTriangle}>
            {t('customBuydown.switchRequest.legacyWarning')}
          </Banner>
        )}

        <ControlledChipsField
          control={control}
          trigger={trigger}
          name="contactEmails"
          label={t('customBuydown.switchRequest.emailLabel')}
          placeholder={t('customBuydown.switchRequest.emailPlaceholder')}
          format={(v) => v.toLowerCase()}
          disabled={disabled}
          required
        />
      </Stack>
    </FieldsetCard>
  )
}

const PlanSummarySidebar = ({
  merchantId,
  activePlan,
  isLocked,
  adminFeeRate,
}: {
  merchantId: string
  activePlan: CustomBuydownPlan | null
  isLocked: boolean
  adminFeeRate: number
}) => {
  const { t, i18n } = useTranslation()
  const context = useBorrowerContext()
  const { methods } = useFormContainerContext<PlanFormValues>()
  const contribution = useWatch({ control: methods.control, name: 'contribution' })
  const maxFinancingAmount = useWatch({ control: methods.control, name: 'maxFinancingAmount' })
  const costReferenceAmount = maxFinancingAmount ?? COST_REFERENCE_AMOUNT

  const probability = conversionProbability(contribution)
  const steps = useMemo(
    () =>
      APPROVAL_TIERS.map(({ threshold, level, color }) => ({
        from: conversionProbability(threshold),
        color,
        label: t(`customBuydown.approval.${level}`),
        description: t(`customBuydown.approvalHint.${level}`),
      })),
    [t],
  )

  return (
    <Card sx={{ position: 'sticky', top: 16 }}>
      <OverlineCardHeader title={t('customBuydown.summaryTitle')} />
      <CardContent>
        <Stack spacing={2.5} divider={<Divider />}>
          <DataField
            labelVariant="eyebrow"
            label={t('customBuydown.planCode')}
            value={<CompactDisplayValue>{previewPlanCode(contribution, adminFeeRate)}</CompactDisplayValue>}
          />
          {activePlan && (
            <DataField
              labelVariant="eyebrow"
              label={t('customBuydown.startDate')}
              value={
                <CompactDisplayValue>{formatEasternDateTime(activePlan.activeFrom, i18n.language)}</CompactDisplayValue>
              }
            />
          )}
          <Stack direction="row" spacing={4}>
            <DataField
              labelVariant="eyebrow"
              label={t('customBuydown.yourFeeRate')}
              value={<CompactDisplayValue>{`${formatPercentage(contribution * 100)}%`}</CompactDisplayValue>}
            />
            <DataField
              labelVariant="eyebrow"
              label={t('customBuydown.estimatedCost', { amount: formatCurrencyNoDecimals(costReferenceAmount) })}
              value={<CompactDisplayValue>{formatCurrencyNoDecimals(contribution * costReferenceAmount)}</CompactDisplayValue>}
            />
          </Stack>
          <StepProgressMeter
            title={t('customBuydown.approvalLikelihood', { context })}
            steps={steps}
            value={probability}
            min={APPROVAL_MODEL.P_MIN}
            max={APPROVAL_MODEL.P_MAX}
            sx={{ '& .MuiLinearProgress-root': { height: 6, mx: 1, width: 'auto' } }}
          />
          <Stack spacing={1.5}>
            <FormSubmitButton label={t('customBuydown.saveAndApply')} fullWidth />
            {activePlan && !isLocked && <TerminatePlanButton merchantId={merchantId} plan={activePlan} />}
            {isLocked && activePlan && (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Icon icon={Lock} size="xs" color="disabled" />
                <Caption>{t('customBuydown.inherited.setByBanner')}</Caption>
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

const useBeforeUnloadGuard = (active: boolean) => {
  useEffect(() => {
    if (!active) return

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [active])
}

const UnsavedChangesGuard = ({ active }: { active: boolean }) => {
  useBeforeUnloadGuard(active)
  const blocker = useBlocker(({ currentLocation, nextLocation }) => active && currentLocation.pathname !== nextLocation.pathname)
  const discard = useConfirmDiscardDialog()

  useEffect(() => {
    if (blocker.state !== 'blocked') return
    void discard.open().then((ok) => (ok ? blocker.proceed() : blocker.reset()))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker.state])

  return null
}

const TerminatePlanButton = ({ merchantId, plan }: { merchantId: string; plan: CustomBuydownPlan }) => {
  const { t } = useTranslation()
  const terminatePlan = useTerminateCustomBuydownPlan(merchantId)
  const confirmTerminate = useConfirmDialog<CustomBuydownPlan>({
    title: (ctx) => t('customBuydown.timeline.terminateConfirmTitle', { planCode: ctx.props.label }),
    message: t('customBuydown.timeline.terminateConfirmMessage'),
    mode: 'destructive',
    confirmLabel: t('customBuydown.timeline.terminateConfirmButton'),
    onError: {
      notFound: (_descriptor, t) => ({ role: 'contextual', message: t('customBuydown.errors.planNotFound', { planCode: plan.label }) }),
      unknown: (descriptor, t) => {
        const code = getPromoPlanErrorCode(descriptor)
        if (code === PromoPlanErrorCode.PlanAlreadyEnded)
          return { role: 'contextual', message: t('customBuydown.errors.planAlreadyEnded', { planCode: plan.label }) }
        return undefined
      },
    },
    onConfirm: (ctx) => terminatePlan.mutateAsync(ctx.props.id),
  })

  return (
    <ActionButton tone="destructive" size="small" startIcon={CircleStop} onClick={() => void confirmTerminate.open(plan)}>
      {t('customBuydown.timeline.terminateAction')}
    </ActionButton>
  )
}

/**
 * The page body, inside the form so it can follow the toggle. The builder and its summary are only
 * worth showing once the merchant is on Custom Buydown or has asked to be — with the toggle off
 * there is nothing to configure and nothing to save, so Save (which lives in the summary) goes too.
 */
const FormBody = ({
  merchantId,
  activePlan,
  switchStatus,
  isLocked,
  needsSwitch,
  adminFeeRate,
  maxLoanAmount,
  buildingPlan,
  onStartCreating,
}: {
  merchantId: string
  activePlan: CustomBuydownPlan | null
  switchStatus: CustomBuydownSwitchStatus
  isLocked: boolean
  needsSwitch: boolean
  adminFeeRate: number
  maxLoanAmount: number
  buildingPlan: boolean
  onStartCreating: () => void
}) => {
  const { t } = useTranslation()
  const context = useBorrowerContext()
  const { methods } = useFormContainerContext<PlanDraftValues>()
  const optIn = useWatch({ control: methods.control, name: 'optIn' })

  const showBuilder = switchStatus.isSwitched || optIn

  return (
    <Grid container columns={12} spacing={3}>
      <UnsavedChangesGuard active={methods.formState.isDirty} />
      <Grid size={{ xs: 12, md: showBuilder ? 8 : 12 }}>
        <Stack spacing={3}>
          {needsSwitch && (
            <SwitchSection
              control={methods.control}
              trigger={methods.trigger}
              hasLegacyPlans={switchStatus.hasLegacyPlans}
              disabled={isLocked}
            />
          )}
          {showBuilder &&
            (buildingPlan ? (
              <FieldsetCard icon={Settings2} title={t('customBuydown.reduceRateTitle', { context })}>
                <Stack spacing={3} divider={<Divider />}>
                  <ControlledNumberField
                    control={methods.control}
                    name="maxFinancingAmount"
                    label={t('customBuydown.maxFinancingAmount')}
                    helperText={t('customBuydown.maxHelper')}
                    isMoney
                    disableDecimals
                    sx={{ width: { xs: '100%', sm: 320 }, maxWidth: '100%' }}
                  />
                  <ContributionSection control={methods.control} context={context} />
                  <RatePreview
                    merchantId={merchantId}
                    control={methods.control}
                    adminFeeRate={adminFeeRate}
                    maxLoanAmount={maxLoanAmount}
                    context={context}
                  />
                </Stack>
              </FieldsetCard>
            ) : (
              <AreaButton
                onClick={onStartCreating}
                icon={<Icon icon={Plus} size="lg" />}
                title={t('customBuydown.timeline.createActiveTitle')}
                description={t('customBuydown.timeline.createActiveDescription')}
              />
            ))}
        </Stack>
      </Grid>
      {showBuilder && (
        <Grid size={{ xs: 12, md: 4 }} sx={{ position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
          <PlanSummarySidebar merchantId={merchantId} activePlan={activePlan} isLocked={isLocked} adminFeeRate={adminFeeRate} />
        </Grid>
      )}
    </Grid>
  )
}

export interface CustomBuydownFormProps {
  merchantId: string
  activePlan: CustomBuydownPlan | null
  switchStatus: CustomBuydownSwitchStatus
  maxLoanAmount: number
}

export const CustomBuydownForm = ({ merchantId, activePlan, switchStatus, maxLoanAmount }: CustomBuydownFormProps) => {
  const { t } = useTranslation()
  const merchant = useMerchant()
  const context = useBorrowerContext()
  const createPlan = useCreateCustomBuydownPlan(merchantId)
  const requestSwitch = useRequestCustomBuydownSwitch(merchantId)
  const adminFeeRate = activePlan?.borrowerFeeRate ?? ADMIN_FEE_RATE
  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    if (activePlan) setIsCreating(false)
  }, [activePlan])

  const isLocked = !!merchant.parentId && (merchant.bannerOwnedFields ?? []).includes(CUSTOM_BUYDOWN_OWNED_FIELD)
  const isBannerMerchant = merchant.merchantTypeId === EMerchantType.Banner

  // The banner lock wins over everything: such a child can neither build plans nor request the switch.
  const needsSwitch = !switchStatus.isSwitched && !isLocked
  const buildingPlan = !!activePlan || isCreating

  if (isLocked && !activePlan) {
    return (
      <Stack spacing={3}>
        <Banner severity="info" icon={Lock}>
          {t('customBuydown.inherited.lockedNotice')}
        </Banner>
        <Card>
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <BodyText>{t('customBuydown.timeline.empty', { context })}</BodyText>
          </CardContent>
        </Card>
      </Stack>
    )
  }

  // A switched merchant with no plan keeps today's bare "create a plan" affordance. An unswitched one
  // must reach Save without building anything, since an empty plan state is a valid switch request.
  if (!buildingPlan && !needsSwitch) {
    return (
      <Stack spacing={3}>
        {isBannerMerchant && <Banner severity="info">{t('customBuydown.owner.bannerNotice')}</Banner>}
        <AreaButton
          onClick={() => setIsCreating(true)}
          icon={<Icon icon={Plus} size="lg" />}
          title={t('customBuydown.timeline.createActiveTitle')}
          description={t('customBuydown.timeline.createActiveDescription')}
        />
      </Stack>
    )
  }

  if (switchStatus.requestedOn) {
    return (
      <Stack spacing={3}>
        <Banner severity="info">{t('customBuydown.switchRequest.pending')}</Banner>
      </Stack>
    )
  }

  return (
    <Stack spacing={3}>
      {isLocked && (
        <Banner severity="info" icon={Lock}>
          {t('customBuydown.inherited.lockedNotice')}
        </Banner>
      )}
      {isBannerMerchant && <Banner severity="info">{t('customBuydown.owner.bannerNotice')}</Banner>}

      <FormContainer
        mode={activePlan ? 'edit' : 'create'}
        disabled={isLocked}
        hideActions
        useFormSetup={() => ({
          schema: planSchema(maxLoanAmount),
          defaultValues: getDefaults(activePlan, switchStatus),
        })}
        onError={{
          unknown: (descriptor) => {
            const messages = getPromoPlanFieldErrorMessages(descriptor)
            return messages.length ? { role: 'contextual', message: messages.join('\n') } : undefined
          },
        }}
        onSubmit={async (values: PlanFormValues) => {
          // Plan first: the switch drops the merchant's legacy plans, so it must never go out unless the
          // replacement exists. A plan left behind by a failed request is inert until the switch and is
          // reused on retry. It is only minted when the builder was opened — an empty switch request is valid.
          const plan = buildingPlan
            ? await createPlan.mutateAsync({
                contribution: values.contribution,
                maxFinancingAmount: values.maxFinancingAmount ?? undefined,
                versionTag: activePlan?.versionTag,
              })
            : null
          const switchResult = needsSwitch ? await requestSwitch.mutateAsync([...values.contactEmails]) : null
          return { plan, switchResult }
        }}
        successMessage={({ plan, switchResult }) =>
          switchResult?.outcome === 'PendingConfirmation'
            ? t('customBuydown.switchRequest.submitted')
            : plan
              ? t('customBuydown.savedSuccess', { planCode: plan.label })
              : t('customBuydown.switchRequest.applied')
        }
      >
        <FormBody
          merchantId={merchantId}
          activePlan={activePlan}
          switchStatus={switchStatus}
          isLocked={isLocked}
          needsSwitch={needsSwitch}
          adminFeeRate={adminFeeRate}
          maxLoanAmount={maxLoanAmount}
          buildingPlan={buildingPlan}
          onStartCreating={() => setIsCreating(true)}
        />
      </FormContainer>
    </Stack>
  )
}
